import { PhraseInput, Spacer, Text } from "@vericus/cadmus-ui";

import { WorkAttachmentSection } from "@/components/work-attachment-section/work-attachment-section";
import { useAppDispatch, useAppSelector } from "@/data/hooks";
import { selectAnswerSnapshot, setLocalSnapshot } from "@/features/authority";
import { readAnswerBlock } from "@/graphql/selectors";
import { Snapshot } from "@/stores/snapshot";

import * as styles from "./answer-form.css";

interface Props {
  answerBlockId: string;
  /** Field identifier */
  identifier: string;
  /** Whether the input is readonly */
  isReadonly?: boolean;
  /** Whether attachment is enabled */
  attachmentEnabled?: boolean;
  /** Whether LDB exam mode is enabled */
  isLdbExamMode?: boolean;
}

/**
 * Answer response section to respond with a numerical range.
 */
export const RangeResponseForm = (props: Props) => {
  const {
    answerBlockId,
    identifier,
    isReadonly = false,
    attachmentEnabled,
    isLdbExamMode,
  } = props;
  const snapshot = useAppSelector((state) =>
    selectAnswerSnapshot(state, answerBlockId)
  );
  const dispatch = useAppDispatch();

  const answerRange =
    snapshot?.answerFields && snapshot.answerFields[identifier]
      ? snapshot.answerFields[identifier]
      : [];

  const answerBlock = readAnswerBlock(answerBlockId);

  const setValue = (number: string, index: number) => {
    const newVersion =
      snapshot?.version === undefined ? 0 : snapshot.version + 1;

    // Ensure `answerRange` has the required length, filling missing elements with "".
    const newAnswerRange = [
      ...answerRange,
      ...Array(Math.max(0, index - answerRange.length + 1)).fill(""),
    ];

    // Update the value at the specified index.
    newAnswerRange[index] = number;

    const newSnapshot: Snapshot = {
      version: newVersion,
      answerFields: {
        [props.identifier]: newAnswerRange,
      },
    };
    dispatch(setLocalSnapshot({ answerBlockId, snapshot: newSnapshot }));
  };

  return (
    <div className={styles.shortAnswerForm}>
      <div className={styles.rangeResponse}>
        <PhraseInput
          type="number"
          placeholder="Type here..."
          onChange={(event) => {
            setValue(event.target.value, 0);
          }}
          value={answerRange?.[0]}
          disabled={isReadonly}
          aria-disabled={isReadonly}
          style={{ minWidth: 240 }}
        />
        <Text kind="bodyMd" color="grey500">
          to
        </Text>
        <PhraseInput
          type="number"
          placeholder="Type here..."
          onChange={(event) => {
            setValue(event.target.value, 1);
          }}
          value={answerRange?.[1]}
          disabled={isReadonly}
          aria-disabled={isReadonly}
          style={{ minWidth: 240 }}
        />
      </div>
      <Spacer spacing={24} />
      {attachmentEnabled && props.answerBlockId && (
        <WorkAttachmentSection
          answerBlockId={props.answerBlockId}
          attachments={answerBlock?.blockAttachments || []}
          disabled={props.isReadonly}
          ldbExamMode={isLdbExamMode}
          mode={"minimal"}
        />
      )}
    </div>
  );
};
